<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none clients-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in clients"
        :key="item._id"
        class="clients-mobile-card"
        :class="{
          'clients-mobile-card--selected': index === activeRow,
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <div class="clients-mobile-card__main">
          <v-checkbox
            :model-value="selected.some(s => s._id === item._id)"
            color="red-darken-3"
            hide-details
            density="compact"
            class="flex-shrink-0 mt-1"
            @change.stop="toggleSelection(item)"
            @click.stop
          />

          <div class="clients-mobile-card__info">
            <div class="clients-mobile-card__info-top">
              <div class="d-flex align-center flex-wrap ga-2">
                <v-chip
                  size="x-small"
                  :color="item.opt_in ? 'success' : 'grey'"
                  variant="tonal"
                >
                  <span v-if="item.opt_in">Optin</span>
                  <span v-else>Portal</span>
                </v-chip>
                <v-btn color="black" variant="outlined" size="x-small" readonly @click.stop>
                  {{ formatDate(item.date_created) }}
                </v-btn>
              </div>

              <div class="font-weight-bold text-primary text-body-2 mt-2">
                {{ item.name }} {{ item.surname }}
              </div>

              <div v-if="item.total_debts != null" class="text-caption mt-1">
                <span class="text-grey-darken-2 font-weight-medium">
                  {{ $t("clients.table.total_debts") }}:
                </span>
                <strong>{{ item.total_debts ? item.total_debts.toFixed(2) : '' }}</strong>
              </div>

              <div v-if="item.email" class="text-caption text-grey-darken-1 mt-1">
                <v-icon size="16" class="mr-1">mdi-email</v-icon>
                {{ item.email }}
              </div>
              <div v-if="item.phone" class="text-caption text-grey-darken-1 mt-1">
                <v-icon size="16" class="mr-1">mdi-phone</v-icon>
                {{ item.phone }}
              </div>
              <div v-if="item.address" class="text-caption text-grey-darken-1 mt-1">
                <v-icon size="16" class="mr-1">mdi-map-marker</v-icon>
                {{ item.address }}
              </div>

              <div v-if="item.comment" class="text-caption mt-1">
                <span class="text-grey-darken-2 font-weight-medium">
                  {{ $t("clients.table.comment") }}:
                </span>
                <span>
                  {{
                    item.comment && item.comment.length > 40
                      ? item.comment.slice(0, 40) + "..."
                      : item.comment
                  }}
                </span>
                <v-btn
                  v-if="item.comment && item.comment.length > 40"
                  variant="text"
                  size="x-small"
                  class="pa-0 ml-1"
                  @click.stop="openCommentModal(item.comment)"
                >
                  <v-icon size="16">mdi-eye</v-icon>
                </v-btn>
              </div>

              <div v-if="item.institution?.title" class="text-caption mt-2">
                <v-chip size="small">{{ item.institution.title }}</v-chip>
              </div>
            </div>
          </div>
        </div>

        <div class="clients-mobile-card__actions">
          <v-spacer />
          <v-btn icon variant="text" size="small" @click.stop="showDetails(item._id)">
            <v-icon size="20" color="grey-darken-2">mdi-format-list-bulleted</v-icon>
          </v-btn>
          <v-btn
            v-if="
              $store.getters.role == 'LekoAdmin' ||
              $store.getters.role == 'LekoEmployee' ||
              $store.getters.role == 'InstitutionAdmin' ||
              $store.getters.role == 'InstitutionManager' ||
              $store.getters.role == 'SuperAdmin'
            "
            icon
            variant="text"
            size="small"
            @click.stop="editClient(item)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="
              $store.getters.role == 'LekoAdmin' ||
              $store.getters.role == 'LekoEmployee' ||
              $store.getters.role == 'InstitutionAdmin' ||
              $store.getters.role == 'InstitutionManager' ||
              $store.getters.role == 'SuperAdmin'
            "
            icon
            variant="text"
            size="small"
            @click.stop="deleteClient(item._id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-alert v-if="!loading && clients.length < 1" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <div class="clients-table-host pb-2">
      <StickyHScroll>
        <v-data-table
          v-model="selected"
          v-model:options="pagination"
          :headers="headers"
          :items="clients"
          :items-length="totalItems"
          :items-per-page="pagination.itemsPerPage"
          :items-per-page-options="[
            { value: 5, title: '5' },
            { value: 10, title: '10' },
            { value: 25, title: '25' },
            { value: 50, title: '50' },
          ]"
          items-per-page-text="Rows per page"
          item-value="_id"
          return-object
          show-select
          :loading="loading"
          class="elevation-1 nowrap-headers pb-8"
          @update:options="updatePagination"
        >
          <template #progress>
            <v-progress-linear color="red" indeterminate />
          </template>
          <template #item="{ item, index }">
            <tr
              :class="{ tableRowColorSelected: index == activeRow }"
              class="tableRowColor"
              @click="activeRow = activeRow === index ? null : index"
            >
              <td>
                <v-checkbox
                  :model-value="selected.includes(item)"
                  color="red-darken-3"
                  hide-details
                  @change="toggleSelection(item)"
                />
              </td>
              <td>
                <span v-if="item.opt_in" class="d-flex"
                  >Optin
                  <v-icon size="small" color="green" class="ml-1"
                    >mdi-check-circle-outline</v-icon
                  ></span
                ><span v-else>Portal</span>
              </td>
              <td>{{ item.name }}</td>
              <td>{{ item.surname }}</td>
              <td>{{ item.total_debts }}</td>
              <td>{{ item.email }}</td>
              <td>{{ item.phone }}</td>
              <td>{{ item.address }}</td>
              <td class="pa-0 ma-0">
                <div class="text-truncate">
                  {{
                    item.comment && item.comment.length > 20
                      ? item.comment.slice(0, 20) + "..."
                      : item.comment
                  }}
                  <a
                    v-if="item.comment && item.comment.length > 20"
                    role="button"
                    @click="openCommentModal(item.comment)"
                    ><small><v-icon>mdi-eye</v-icon></small></a
                  >
                </div>
              </td>
              <td v-if="item.institution">
                <v-chip>
                  {{ item.institution.title }}
                </v-chip>
              </td>
              <td v-else />
              <td>
                <v-btn color="black" variant="outlined" size="small">
                  {{ formatDate(item.date_created) }}
                </v-btn>
              </td>
              <td>
                <v-row class="d-flex flex-nowrap">
                  <v-icon
                    class="mr-2 pointer"
                    color="grey-darken-2"
                    @click="showDetails(item._id)"
                  >
                    mdi-format-list-bulleted
                  </v-icon>
                  <v-icon
                    v-if="
                      $store.getters.role == 'LekoAdmin' ||
                      $store.getters.role == 'LekoEmployee' ||
                      $store.getters.role == 'InstitutionAdmin' ||
                      $store.getters.role == 'InstitutionManager' ||
                      $store.getters.role == 'SuperAdmin'
                    "
                    class="mr-2 pointer"
                    color="grey-darken-2"
                    @click="editClient(item)"
                  >
                    mdi-pencil
                  </v-icon>
                  <v-icon
                    v-if="
                      $store.getters.role == 'LekoAdmin' ||
                      $store.getters.role == 'LekoEmployee' ||
                      $store.getters.role == 'InstitutionAdmin' ||
                      $store.getters.role == 'InstitutionManager' ||
                      $store.getters.role == 'SuperAdmin'
                    "
                    class="pointer"
                    color="grey-darken-2"
                    @click="deleteClient(item._id)"
                  >
                    mdi-delete
                  </v-icon>
                </v-row>
              </td>
            </tr>
          </template>
        </v-data-table>
      </StickyHScroll>
    </div>

    <v-dialog v-model="commentModal" width="300">
      <v-card>
        <div
          class="col-12 text-end justify-end"
          style="text-align: end !important"
        >
          <v-icon @click="closeCommentModal()"> mdi-close </v-icon>
        </div>
        <div class="pa-3">
          {{ commentText }}
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogDelete" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert class="mb-0" :value="true" type="error">
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDelete = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete()">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

import getRequest from "../../api/getRequest";
import deleteRequest from "../../api/deleteRequest";

export default {
  props: ["search"], // eslint-disable-line vue/require-prop-types
  data: () => ({
    activeRow: null,
    loading: false,
    headers: [
      {
        title: "",
        sortable: false,
        key: "optin",
      },
      {
        title: "",
        sortable: false,
        key: "name",
      },
      {
        title: "",
        sortable: false,
        key: "surname",
      },
      {
        title: "Total Debts",
        sortable: true,
        key: "total_debts",
      },
      {
        title: "",
        sortable: false,
        key: "email",
      },
      {
        title: "",
        sortable: false,
        key: "phone",
      },
      {
        title: "",
        sortable: false,
        key: "address",
      },
      {
        title: "",
        sortable: false,
        key: "comment",
      },
      {
        title: "",
        sortable: false,
        key: "institution",
      },
      {
        title: "",
        sortable: true,
        key: "",
      },
      {
        title: "",
        sortable: false,
        key: "actions",
      },
    ],
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: { key: "date_created", order: "desc" },
      sortDesc: [true],
    },
    selected: [],
    clients: [],
    totalItems: 0,
    commenttitle: "",
    commentModal: false,
    dialogDelete: false,
    clientToDelete: null,
  }),
  computed: {
    searchField() {
      return this.$props.search;
    },
  },
  watch: {
    selected: {
      handler(data) {
        if (data.length > 0) {
          this.$emit("selected", { value: true, items: data });
        } else {
          this.$emit("selected", { value: false, items: data });
        }
      },
      deep: true,
      immediate: true,
    },
    pagination: {
      handler() {
        if (!this.searchField || this.searchField.length < 1) this.getClients();
        else this.searchClients(this.searchField);
      },
    },
    searchField: {
      handler(value) {
        if (!value || value.length < 1) this.getClients();
        else this.searchClients(value);
      },
      deep: true,
    },
  },
  created() {
    this.getLocale();
    this.getClients();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("clients.table.opt_in");
      this.headers[1].title = this.$t("clients.table.name");
      this.headers[2].title = this.$t("clients.table.surname");
      this.headers[3].title = this.$t("clients.table.total_debts");
      this.headers[4].title = this.$t("clients.table.email");
      this.headers[5].title = this.$t("clients.table.phone");
      this.headers[6].title = this.$t("clients.table.address");
      this.headers[7].title = this.$t("clients.table.comment");
      this.headers[8].title = this.$t("clients.table.institution");
      this.headers[9].title = this.$t("clients.table.date_created");
      this.headers[10].title = this.$t("clients.table.actions");
    },
    getClients() {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/client/",
        `role=${this.$store.getters.role}&institution_id=${
          this.$store.getters.institutionId
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.clients = response.clients;
          this.totalItems = response.count;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    searchClients(value) {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/client/",
        `role=${this.$store.getters.role}&search=${value}&institution_id=${
          this.$store.getters.institutionId
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.clients = response.clients;
          this.totalItems = response.count;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    showDetails(id) {
      this.$emit("details", id);
    },
    editClient(data) {
      this.$emit("edit", data);
    },
    deleteClient(id) {
      this.clientToDelete = id;
      this.dialogDelete = true;
    },
    confirmDelete() {
      this.loading = true;
      deleteRequest("/client/", `id=${this.clientToDelete}`)
        .then((response) => {
          if (response) {
            this.loading = false;
            this.dialogDelete = false;
            this.clientToDelete = null;
            this.getClients();
          }
        })
        .catch((error) => {
          if (error) {
            this.loading = false;
            this.dialogDelete = false;
            this.clientToDelete = null;
          }
        });
    },
    openCommentModal(text) {
      this.commentText = text;
      this.commentModal = true;
    },
    closeCommentModal() {
      this.commentText = "";
      this.commentModal = false;
    },
    toggleSelection(item) {
      const index = this.selected.findIndex((s) => s._id === item._id);
      if (index === -1) {
        this.selected.push(item);
      } else {
        this.selected.splice(index, 1);
      }
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .clients-table-host :deep(.v-table__wrapper),
  .clients-table-host :deep(.v-data-table__wrapper),
  .clients-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .clients-table-host :deep(.global-sticky-hscroll) {
    display: none !important;
  }
  .clients-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .clients-mobile-list {
    display: none !important;
  }
}

.clients-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.clients-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.clients-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;
}
.clients-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.clients-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 6px;
}
</style>
